import { isAnswered, type WizardQuestion, type WizardValue } from '../questions/answer-wizard-model';
import { CODING_CONTEXT_MAX_CHARS } from './coding-events';

/**
 * FRONT-3138: the new-project screen is the Builder's reply box, and the
 * question band on top of it asks the opening beats of EDDY's own guided
 * interview (src/coding/prompt.ts, GUIDED_BUILD_LINES) before the project
 * exists. Every question is optional — the interview still runs, it just
 * starts further along, exactly as the old free-text context box did.
 */
export const NEW_PROJECT_QUESTIONS: WizardQuestion[] = [
  {
    header: 'Who',
    question: 'Who is it for?',
    multiSelect: false,
    options: [
      { label: 'Just me' },
      { label: 'My team' },
      { label: 'My customers' },
      { label: 'Anyone on the web' },
    ],
  },
  {
    header: 'Kind',
    question: 'What kind of thing is it?',
    multiSelect: false,
    options: [
      { label: 'An app that keeps data', description: 'Lists, records, a small tracker' },
      { label: 'A website or page', description: 'Something people read' },
      { label: 'A dashboard', description: 'Numbers and charts at a glance' },
      { label: 'A tool or calculator', description: 'Put something in, get something out' },
    ],
  },
  {
    header: 'Look',
    question: 'How should it look and feel?',
    multiSelect: false,
    options: [
      { label: 'Calm and minimal' },
      { label: 'Bold and colourful' },
      { label: 'Playful' },
      { label: 'Like our brand' },
    ],
  },
];

/**
 * What EDDY reads as the project's up-front context: the typed text, then one
 * line per answered question. Skipped and open questions say nothing — EDDY
 * asks them himself. Capped at the context file's own limit, answers first so
 * a long paste can never squeeze them out.
 */
export function newProjectContext(text: string, value: WizardValue, questions = NEW_PROJECT_QUESTIONS): string {
  const answers = questions
    .map((q, i) => (isAnswered(value, i) ? `${q.question} ${(value.selected[i] ?? []).join(', ')}` : null))
    .filter((line): line is string => line !== null);
  const typed = text.trim();
  const answerBlock = answers.join('\n');
  const room = CODING_CONTEXT_MAX_CHARS - answerBlock.length - (answerBlock && typed ? 2 : 0);
  const body = typed.slice(0, Math.max(0, room));
  return [body, answerBlock].filter(Boolean).join('\n\n');
}

/**
 * Whether the reply box's Start may create the project, and why not. Derived
 * from the reason (CLAUDE.md, FRONT-1670): Start is off exactly when there is
 * a sentence to show under the box. The name is optional — left empty, EDDY
 * names the project in his first reply (eddy, 2026-10-26).
 */
export function newProjectStartGate(input: { creating: boolean; uploading: boolean }): {
  disabled: boolean;
  reason?: string;
} {
  if (input.creating) return { disabled: true, reason: 'Creating the project…' };
  if (input.uploading) return { disabled: true, reason: 'Wait for the images to finish uploading' };
  return { disabled: false };
}
